import { useContext, useEffect, useState } from 'react';
import { Container, Typography, Box, Button, Paper, Grid, Chip } from '@mui/material';
import femme from '../assets/images/femme.png';
import { Link } from 'react-router-dom';
import AuthContext from '../contexts/AuthContext';
import EventNoteIcon from '@mui/icons-material/EventNote';
import EventIcon from '@mui/icons-material/Event';
import LoginIcon from '@mui/icons-material/Login';
import PeopleIcon from '@mui/icons-material/People';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import BusinessIcon from '@mui/icons-material/Business';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import SecurityIcon from '@mui/icons-material/Security';
import SpeedIcon from '@mui/icons-material/Speed';
import VerifiedIcon from '@mui/icons-material/Verified';

// Palette officielle
const RED   = '#d21433';
const GOLD  = '#ffc71f';
const GREEN = '#007481';

const features = [
  { icon: <EventNoteIcon sx={{ fontSize: 40 }} />,      title: 'Gestion des Visites',        desc: 'Planifiez et suivez toutes les visites en temps réel',         color: RED   },
  { icon: <AssignmentIndIcon sx={{ fontSize: 40 }} />,  title: 'Absences Administratives',   desc: 'Gérez les absences avec traçabilité complète',                 color: GREEN },
  { icon: <SecurityIcon sx={{ fontSize: 40 }} />,       title: 'Sécurité Renforcée',         desc: 'Vérification OTP et contrôle d\'accès avancé',                color: GOLD  },
  { icon: <SpeedIcon sx={{ fontSize: 40 }} />,          title: 'Rapide & Efficace',          desc: 'Interface intuitive pour une productivité maximale',           color: RED   },
];

function AnimatedParticle({ style }) {
  return (
    <Box
      sx={{
        position: 'absolute',
        borderRadius: '50%',
        background: 'rgba(255,255,255,0.12)',
        animation: 'floatParticle 6s ease-in-out infinite',
        ...style,
        '@keyframes floatParticle': {
          '0%, 100%': { transform: 'translateY(0px) scale(1)', opacity: 0.5 },
          '50%':       { transform: 'translateY(-28px) scale(1.1)', opacity: 0.9 },
        },
      }}
    />
  );
}

function Home() {
  const { isAuthenticated, user } = useContext(AuthContext);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  return (
    <Box sx={{ minHeight: '100vh', background: '#ffffff', pb: 6 }}>

      {/* ── Hero Section ── */}
      <Box
        sx={{
          position: 'relative',
          overflow: 'hidden',
          background: `linear-gradient(135deg, #003d42 0%, #005a63 25%, #006d78 50%, #008a99 75%, #00a3b5 100%)`,
          color: 'white',
          py: { xs: 5, md: 8 },
          px: 2,
          mb: 6,
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0, left: 0, right: 0, bottom: 0,
            background: 'radial-gradient(ellipse at 75% 40%, rgba(255,199,31,0.12) 0%, transparent 60%)',
          },
        }}
      >
        {/* Particules flottantes */}
        <AnimatedParticle style={{ width: 80,  height: 80,  top: '10%', left: '5%',   animationDelay: '0s'   }} />
        <AnimatedParticle style={{ width: 50,  height: 50,  top: '60%', left: '15%',  animationDelay: '1s'   }} />
        <AnimatedParticle style={{ width: 120, height: 120, top: '20%', right: '8%',  animationDelay: '2s'   }} />
        <AnimatedParticle style={{ width: 60,  height: 60,  bottom: '15%', right: '20%', animationDelay: '0.5s' }} />
        <AnimatedParticle style={{ width: 40,  height: 40,  top: '40%', right: '35%', animationDelay: '1.5s' }} />

        {/* Bande tricolore verticale décorative */}
        <Box sx={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 6, background: `linear-gradient(180deg, ${RED}, ${GOLD}, ${GREEN})` }} />

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1, textAlign: 'center', mt: -3 }}>
          {/* Badge officiel */}
          <Box
            sx={{
              display: 'inline-flex', alignItems: 'center', gap: 1,
              background: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255,255,255,0.3)', borderRadius: 5,
              px: 2.5, py: 0.8, mb: 3,
              opacity: visible ? 1 : 0,
              transform: visible ? 'translateY(0)' : 'translateY(-20px)',
              transition: 'all 0.6s ease',
            }}
          >
            <VerifiedIcon sx={{ fontSize: 18, color: GOLD }} />
            <Typography variant="body2" sx={{ fontWeight: 600, letterSpacing: 1 }}>
              SYSTÈME OFFICIEL DE GESTION
            </Typography>
          </Box>

          {/* Titre */}
          <Typography
            variant="h3"
            component="h1"
            sx={{
              fontWeight: 800, mb: 2, lineHeight: 1.2,
              fontSize: { xs: '1.9rem', md: '3rem' },
              textShadow: '0 2px 20px rgba(0,0,0,0.25)',
              opacity: visible ? 1 : 0,
              transform: visible ? 'translateY(0)' : 'translateY(30px)',
              transition: 'all 0.7s ease 0.1s',
            }}
          >
            Gestion Électronique des{' '}
            <Box
              component="span"
              sx={{ color: '#ffffff', fontWeight: 900, fontSize: { xs: '2.1rem', md: '3.4rem' }, textShadow: '0 2px 18px rgba(255,255,255,0.35)' }}
            >
              Visites & Absences
            </Box>
          </Typography>

          <Typography
            component="p"
            sx={{
              mb: 5, fontWeight: 400, maxWidth: 640, mx: 'auto', lineHeight: 1.55, fontSize: { xs: '0.88rem', md: '0.92rem' },
              opacity: visible ? 0.9 : 0,
              transform: visible ? 'translateY(0)' : 'translateY(20px)',
              transition: 'all 0.7s ease 0.2s',
            }}
          >
            La Plateforme de Gestion Électronique des Visites et Absences Administratives (<Box component="span" sx={{ fontWeight: 700 }}>GEVAA</Box>) modernise la gestion des rendez-vous, visites et absences administratives.
          </Typography>

          {/* Boutons visiteur non connecté */}
          {!isAuthenticated && (
            <Box
              sx={{
                display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap', mt: -2,
                opacity: visible ? 1 : 0,
                transform: visible ? 'translateY(0)' : 'translateY(20px)',
                transition: 'all 0.7s ease 0.3s',
              }}
            >
              <Button
                component={Link} to="/appointment"
                variant="contained" size="large" startIcon={<EventNoteIcon />}
                sx={{
                  background: `linear-gradient(135deg, ${GOLD} 0%, #e6b000 100%)`,
                  color: '#3d2000', fontWeight: 800,
                  px: 4, py: 1.5, borderRadius: 3, fontSize: '1rem',
                  boxShadow: `0 8px 30px rgba(255,199,31,0.4)`,
                  transition: 'all 0.3s ease',
                  '&:hover': { transform: 'translateY(-3px)', boxShadow: `0 12px 40px rgba(255,199,31,0.55)`, background: GOLD },
                }}
              >
                Prendre un rendez-vous
              </Button>
              <Button
                component={Link} to="/login"
                variant="outlined" size="large" startIcon={<LoginIcon />}
                sx={{
                  borderColor: 'rgba(255,255,255,0.7)', color: 'white',
                  fontWeight: 600, px: 4, py: 1.5, borderRadius: 3, fontSize: '1rem',
                  backdropFilter: 'blur(10px)', background: 'rgba(255,255,255,0.1)',
                  transition: 'all 0.3s ease',
                  '&:hover': { background: 'rgba(255,255,255,0.2)', borderColor: 'white', transform: 'translateY(-3px)' },
                }}
              >
                Se connecter
              </Button>
            </Box>
          )}

          {/* Badge utilisateur connecté */}
          {isAuthenticated && (
            <Box sx={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(20px)', transition: 'all 0.7s ease 0.3s' }}>
              <Box
                sx={{
                  display: 'inline-flex', alignItems: 'center', gap: 1.5,
                  background: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255,255,255,0.3)', borderRadius: 3, px: 3, py: 1.5,
                }}
              >
                <AccountCircleIcon sx={{ fontSize: 28 }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Bonjour, {user.firstName} {user.lastName} !
                </Typography>
                <Chip
                  label={user.role}
                  size="small"
                  sx={{ background: GOLD, color: '#3d2000', fontWeight: 800, fontSize: '0.7rem' }}
                />
              </Box>
            </Box>
          )}
        </Container>

        {/* Vague de transition */}
        <Box
          sx={{
            position: 'absolute', bottom: -2, left: 0, right: 0, height: 60,
            background: '#ffffff',
            clipPath: 'ellipse(55% 100% at 50% 100%)',
          }}
        />
      </Box>

      <Container maxWidth="lg">

        {/* ── Image + Avantages ── */}
        {!isAuthenticated && (
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: 'center', gap: 5, mb: 6, mt: 2 }}>
            {/* Colonne image */}
            <Box sx={{ flex: '0 0 auto', textAlign: 'center' }}>
              <Box
                component="img"
                src={femme}
                alt="Collaboratrice ANDE"
                sx={{
                  maxHeight: { xs: 240, md: 360 },
                  width: 'auto',
                  maxWidth: '100%',
                  objectFit: 'contain',
                  filter: 'drop-shadow(0 8px 24px rgba(0,0,0,0.12))',
                }}
              />
            </Box>

            {/* Colonne avantages */}
            <Box sx={{ flex: 1 }}>
              <Typography variant="h4" sx={{ fontWeight: 900, mb: 1.5, color: GREEN, fontSize: { xs: '1.6rem', md: '2rem' } }}>
                Pourquoi GEVAA ?
              </Typography>
              <Typography variant="body1" sx={{ color: '#444', mb: 3.5, fontSize: '1rem', lineHeight: 1.7 }}>
                Une solution complète pour moderniser la gestion administrative de votre institution.
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2.5 }}>
                {[
                  { icon: '✅', title: 'Suivi en temps réel', desc: 'Visites et absences tracées instantanément.' },
                  { icon: '🔐', title: 'Sécurité OTP', desc: 'Confirmation par code à usage unique.' },
                  { icon: '📊', title: 'Tableaux de bord', desc: 'Statistiques claires pour les décideurs.' },
                  { icon: '🏛️', title: 'Multi-départements', desc: 'Adapté à la structure de chaque institution.' },
                  { icon: '⚡', title: 'Rapide & intuitif', desc: 'Prise en main immédiate, zéro formation.' },
                ].map((item) => (
                  <Box key={item.title} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                    <Box sx={{ fontSize: '2.2rem', flexShrink: 0 }}>{item.icon}</Box>
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#1a1a1a', lineHeight: 1.3, fontSize: '1.05rem' }}>
                        {item.title}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#555', fontSize: '0.92rem', mt: 0.3, lineHeight: 1.5 }}>
                        {item.desc}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        )}

        {/* ── Fonctionnalités (non connecté) ── */}
        {false && !isAuthenticated && (
          <Box sx={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(40px)', transition: 'all 0.8s ease 0.4s', mb: 6 }}>
            <Typography variant="h5" textAlign="center" sx={{ fontWeight: 700, mb: 4, color: '#1a1a1a' }}>
              Fonctionnalités principales
            </Typography>
            <Grid container spacing={3}>
              {features.map((feature, index) => (
                <Grid item xs={12} sm={6} md={3} key={index}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3, textAlign: 'center', borderRadius: 3,
                      border: `1px solid ${feature.color}22`,
                      background: 'white',
                      transition: 'all 0.3s ease',
                      cursor: 'default',
                      opacity: visible ? 1 : 0,
                      transform: visible ? 'translateY(0)' : 'translateY(30px)',
                      transitionDelay: `${0.5 + index * 0.1}s`,
                      '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: `0 20px 40px ${feature.color}25`,
                        borderColor: feature.color,
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: 75, height: 75, borderRadius: '50%',
                        background: `linear-gradient(135deg, ${feature.color}20, ${feature.color}40)`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        mx: 'auto', mb: 2, color: feature.color,
                      }}
                    >
                      {feature.icon}
                    </Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1, color: '#1a1a1a' }}>
                      {feature.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                      {feature.desc}
                    </Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        {/* ── Dashboard utilisateur connecté ── */}
        {isAuthenticated && (
          <Box sx={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(40px)', transition: 'all 0.8s ease 0.4s' }}>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 3, color: '#1a1a1a', textAlign: 'center' }}>
              Accès rapide
            </Typography>

            {['ADMIN', 'MANAGER'].includes(user?.role) && (
              <Grid container spacing={3}>
                {[
                  { to: '/visits',   label: 'Gestion des visites',   icon: <PeopleIcon sx={{ fontSize: 36 }} />,             color: RED,   desc: 'Gérer et suivre les visites'       },
                  { to: '/absences', label: 'Gestion des absences',  icon: <AssignmentIndIcon sx={{ fontSize: 36 }} />,      color: GREEN, desc: 'Suivre les absences admin'          },
                  { to: '/profile',  label: 'Mon profil',            icon: <AccountCircleIcon sx={{ fontSize: 36 }} />,      color: GOLD,  desc: 'Mes informations personnelles'     },
                  { to: '/users',    label: 'Administration',        icon: <AdminPanelSettingsIcon sx={{ fontSize: 36 }} />, color: RED,   desc: 'Gérer les utilisateurs'            },
                ].map((item, i) => (
                  <Grid item xs={12} sm={6} md={3} key={i}>
                    <DashCard item={item} visible={visible} delay={0.5 + i * 0.1} />
                  </Grid>
                ))}
              </Grid>
            )}

            {['HOST', 'ACCEUIL'].includes(user?.role) && (
              <Grid container spacing={3} justifyContent="center">
                {[
                  { to: '/appointments', label: 'Rendez-vous', icon: <EventIcon sx={{ fontSize: 36 }} />, color: RED, desc: 'Gérer les rendez-vous' },
                  { to: '/visits', label: 'Visites', icon: <PeopleIcon sx={{ fontSize: 36 }} />, color: GREEN, desc: 'Consulter les visites' },
                  { to: '/profile', label: 'Mon profil', icon: <AccountCircleIcon sx={{ fontSize: 36 }} />, color: GOLD, desc: 'Mes informations' },
                ].map((item, i) => (
                  <Grid item xs={12} sm={6} md={4} key={i}>
                    <DashCard item={item} visible={visible} delay={0.5 + i * 0.1} />
                  </Grid>
                ))}
              </Grid>
            )}

            {['EMPLOYEE', 'RH'].includes(user?.role) && (
              <Grid container spacing={3} justifyContent="center">
                {[
                  { to: '/visits',   label: 'Gestion des visites',  icon: <PeopleIcon sx={{ fontSize: 36 }} />,            color: RED,   desc: 'Consulter les visites' },
                  { to: '/absences', label: 'Gestion des absences', icon: <AssignmentIndIcon sx={{ fontSize: 36 }} />,     color: GREEN, desc: 'Mes absences'          },
                  { to: '/profile',  label: 'Mon profil',           icon: <AccountCircleIcon sx={{ fontSize: 36 }} />,     color: GOLD,  desc: 'Mes informations'     },
                ].map((item, i) => (
                  <Grid item xs={12} sm={6} md={4} key={i}>
                    <DashCard item={item} visible={visible} delay={0.5 + i * 0.1} />
                  </Grid>
                ))}
              </Grid>
            )}

            {['VISITOR'].includes(user?.role) && (
              <Grid container spacing={3} justifyContent="center">
                {[
                  { to: '/appointments', label: 'Rendez-vous', icon: <EventIcon sx={{ fontSize: 36 }} />, color: RED, desc: 'Suivre mes rendez-vous' },
                  { to: '/profile', label: 'Mon profil', icon: <AccountCircleIcon sx={{ fontSize: 36 }} />, color: GOLD, desc: 'Mes informations personnelles' },
                ].map((item, i) => (
                  <Grid item xs={12} sm={6} md={4} key={i}>
                    <DashCard item={item} visible={visible} delay={0.5 + i * 0.1} />
                  </Grid>
                ))}
              </Grid>
            )}
          </Box>
        )}

        {/* ── CTA bas de page (non connecté) ── */}
        {!isAuthenticated && (
          <Box sx={{ mt: 4, textAlign: 'center', opacity: visible ? 1 : 0, transition: 'all 0.8s ease 0.9s' }}>
            <Paper
              elevation={0}
              sx={{
                p: 4, borderRadius: 4,
                background: `linear-gradient(135deg, #005a63, ${GREEN})`,
                color: 'white',
                position: 'relative', overflow: 'hidden',
                '&::before': {
                  content: '""', position: 'absolute',
                  top: '-50%', right: '-10%',
                  width: 250, height: 250, borderRadius: '50%',
                  background: 'rgba(255,255,255,0.06)',
                },
              }}
            >
              <BusinessIcon sx={{ fontSize: 50, mb: 2, opacity: 0.9 }} />
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
                Vous êtes un collaborateur ?
              </Typography>
              <Typography variant="body1" sx={{ mb: 3, opacity: 0.85 }}>
                Connectez-vous pour accéder à toutes les fonctionnalités de gestion.
              </Typography>
              <Button
                component={Link} to="/login"
                variant="contained" size="large" startIcon={<LoginIcon />}
                sx={{
                  background: 'white', color: GREEN, fontWeight: 700,
                  px: 4, py: 1.2, borderRadius: 3,
                  '&:hover': { background: '#f0fafb', transform: 'translateY(-2px)' },
                  transition: 'all 0.3s ease',
                }}
              >
                Se connecter
              </Button>
            </Paper>
          </Box>
        )}
      </Container>
    </Box>
  );
}

// Carte du dashboard réutilisable
function DashCard({ item, visible, delay }) {
  return (
    <Paper
      component={Link}
      to={item.to}
      elevation={0}
      sx={{
        p: 3, textAlign: 'center', borderRadius: 3,
        border: `2px solid ${item.color}22`,
        background: `linear-gradient(135deg, white, ${item.color}08)`,
        textDecoration: 'none', display: 'block',
        transition: 'all 0.3s ease',
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(30px)',
        transitionDelay: `${delay}s`,
        '&:hover': {
          transform: 'translateY(-8px)',
          boxShadow: `0 20px 40px ${item.color}30`,
          borderColor: item.color,
          background: `linear-gradient(135deg, white, ${item.color}15)`,
        },
      }}
    >
      <Box sx={{ color: item.color, mb: 1.5 }}>{item.icon}</Box>
      <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1a1a1a', mb: 0.5 }}>
        {item.label}
      </Typography>
      <Typography variant="body2" color="text.secondary">{item.desc}</Typography>
    </Paper>
  );
}

export default Home;
